import React, { useEffect, useState } from "react";
import { Card, Collapse, Table, Spin, Empty, Row, Col, Statistic, Tag, Typography, Tooltip, Space } from "antd";
import { getCargoReport } from "../../api/flightOperationApi";

const { Text } = Typography;

function buildAwbColumns(canDownload) {
    const columns = [
        {
            title: "主单号",
            dataIndex: "awb_number",
            width: 140,
            render: (v) => <Text strong>{v}</Text>,
        },
        {
            title: "航线",
            render: (_, r) => `${r.origin || "—"}→${r.destination || "—"}`,
            width: 100,
        },
        { title: "件数", dataIndex: "pieces", width: 55 },
        {
            title: "净重",
            dataIndex: "weight_kg",
            width: 90,
            render: (v) => (v != null ? `${v.toLocaleString()} kg` : "—"),
        },
        {
            title: "体积",
            width: 90,
            render: (_, r) => {
                const v = r.fwb_volume_cbm ?? r.volume_cbm;
                return v != null ? `${Number(v).toLocaleString()} m³` : "—";
            },
        },
        {
            title: "品名",
            dataIndex: "fwb_description",
            ellipsis: true,
            render: (v, r) => {
                const desc = v || r.description;
                if (!desc) return "—";
                if (!canDownload) return desc.length > 24 ? `${desc.slice(0, 24)}…` : desc;
                return desc;
            },
        },
        {
            title: "SHC",
            dataIndex: "shc",
            width: 80,
            render: (v) => v || "—",
        },
    ];
    if (canDownload) {
        columns.push(
            {
                title: "发货人",
                dataIndex: "fwb_shipper",
                ellipsis: true,
                width: 120,
                render: (v) => v || "—",
            },
            {
                title: "收货人",
                dataIndex: "fwb_consignee",
                ellipsis: true,
                width: 120,
                render: (v) => v || "—",
            },
        );
    }
    return columns;
}

function sumWeight(items, getWeight = (item) => item.weight_kg) {
    const weights = items
        .map(getWeight)
        .filter((weight) => weight != null && Number.isFinite(Number(weight)))
        .map(Number);
    return weights.length > 0 ? weights.reduce((total, weight) => total + weight, 0) : null;
}

function uldPrefixMatch(a, b) {
    if (a === b) return true;
    const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
    return shorter.length >= 9 && longer.startsWith(shorter);
}

function findUldGroup(groups, uldId) {
    const exact = groups.get(uldId);
    if (exact) return [uldId, exact];
    for (const [key, group] of groups) {
        if (uldPrefixMatch(uldId, key)) return [key, group];
    }
    return [null, null];
}

function hasUldInSet(uldId, idSet) {
    if (idSet.has(uldId)) return true;
    for (const id of idSet) {
        if (uldPrefixMatch(uldId, id)) return true;
    }
    return false;
}

function buildUldGroups(cpm, ffm) {
    const groups = new Map();
    const ffmUldIds = new Set((ffm?.ulds || []).map((u) => u.uld_id));
    const cpmUldIds = new Set(
        (cpm?.positions || [])
            .filter((p) => !p.is_empty && p.uld_id)
            .map((p) => p.uld_id),
    );
    const hasBothSources = ffmUldIds.size > 0 && cpmUldIds.size > 0;

    (ffm?.ulds || []).forEach((uld) => {
        groups.set(uld.uld_id, {
            key: uld.uld_id,
            uldId: uld.uld_id,
            positions: [],
            awbs: uld.awbs || [],
        });
    });

    (cpm?.positions || [])
        .filter((position) => !position.is_empty && position.uld_id)
        .forEach((position) => {
            const [matchKey, existing] = findUldGroup(groups, position.uld_id);
            const group = existing || {
                key: position.uld_id,
                uldId: position.uld_id,
                positions: [],
                awbs: [],
            };
            group.positions.push(position);
            if (!existing) groups.set(position.uld_id, group);
        });

    const result = Array.from(groups.values()).map((group) => {
        let sourceTag = null;
        if (hasBothSources && group.uldId) {
            const inCpm = hasUldInSet(group.uldId, cpmUldIds);
            const inFfm = hasUldInSet(group.uldId, ffmUldIds);
            if (inCpm && !inFfm) sourceTag = "CPM_ONLY";
            else if (inFfm && !inCpm) sourceTag = "FFM_ONLY";
        }
        return {
            ...group,
            sourceTag,
            netWeightKg: sumWeight(group.positions, (p) => p.net_weight_kg) ?? sumWeight(group.awbs),
            grossWeightKg: sumWeight(group.positions, (p) => p.weight_kg),
            heights: group.positions.map((p) => p.height).filter(Boolean),
        };
    });

    const bulkAwbs = ffm?.bulk_cargo || [];
    const bulkPositions = (cpm?.positions || []).filter(
        (position) => !position.is_empty && !position.uld_id,
    );
    if (bulkAwbs.length > 0 || bulkPositions.length > 0) {
        result.push({
            key: "BULK",
            uldId: null,
            sourceTag: null,
            positions: bulkPositions,
            awbs: bulkAwbs,
            netWeightKg: sumWeight(bulkPositions, (p) => p.net_weight_kg) ?? sumWeight(bulkAwbs),
            grossWeightKg: sumWeight(bulkPositions, (p) => p.weight_kg),
            heights: bulkPositions.map((p) => p.height).filter(Boolean),
        });
    }

    return result;
}

function renderWeight(weight) {
    return weight != null ? `${weight.toLocaleString()} kg` : "—";
}

function buildUldCollapseItem(group, canDownload) {
    const positionCodes = group.positions
        .map((position) => position.position_code)
        .filter(Boolean)
        .join("、");
    const unknownPosition = group.positions.length > 0 && !positionCodes;

    return {
        key: group.key,
        label: (
            <Space wrap size={12}>
                <Text strong>{group.uldId || "散货"}</Text>
                {group.sourceTag === "CPM_ONLY" && (
                    <Tooltip title="该 ULD 仅出现在装载报文（CPM）中，舱单（FFM）未包含">
                        <Tag color="orange">仅 CPM</Tag>
                    </Tooltip>
                )}
                {group.sourceTag === "FFM_ONLY" && (
                    <Tooltip title="该 ULD 仅出现在舱单（FFM）中，装载报文（CPM）未包含">
                        <Tag color="orange">仅 FFM</Tag>
                    </Tooltip>
                )}
                {positionCodes ? (
                    <Text type="secondary">舱位 {positionCodes}</Text>
                ) : unknownPosition ? (
                    <Text type="secondary">舱位未知</Text>
                ) : null}
                {group.heights?.length > 0 && <Text type="secondary">高度 {group.heights.join("、")}</Text>}
                <Text type="secondary">{group.awbs.length} 票主单</Text>
                <Text>净重 {renderWeight(group.netWeightKg)}</Text>
                <Text>含板 {renderWeight(group.grossWeightKg)}</Text>
            </Space>
        ),
        children: group.awbs.length > 0 ? (
            <Table
                dataSource={group.awbs}
                rowKey={(awb, index) => `${awb.awb_number}-${index}`}
                size="small"
                pagination={false}
                columns={buildAwbColumns(canDownload)}
            />
        ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无舱单主单数据" />
        ),
    };
}

/**
 * @param {object} props
 * @param {number} [props.flightId] 航班 ID；未提供 reportData 时走运营接口拉取
 * @param {object} [props.reportData] 销售档案已聚合的详情（优先使用，避免绕开销售权限）
 * @param {boolean} [props.showSummary]
 * @param {boolean} [props.canDownload] 默认 false：无下载权不展示敏感列
 */
export default function CargoReportDetail({
    flightId,
    reportData = null,
    showSummary = true,
    canDownload = false,
}) {
    const [report, setReport] = useState(reportData);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (reportData) {
            setReport(reportData);
            return;
        }
        if (!flightId) return;
        setLoading(true);
        getCargoReport(flightId)
            .then((res) => setReport(res?.data))
            .catch(() => setReport(null))
            .finally(() => setLoading(false));
    }, [flightId, reportData]);

    if (loading) return <Spin style={{ display: "block", textAlign: "center", padding: 40 }} />;
    if (!report?.summary && !report?.cpm && !report?.ffm) return <Empty description="暂无载量数据" />;

    const { summary = {}, cpm, ffm, data_sources, ffm_completeness, fact_summary } = report;
    const fwbLinked = summary.fwb_linked_count ?? fact_summary?.fwb?.linked_awb_count ?? 0;
    const awbTotal = summary.awb_count ?? fact_summary?.fwb?.awb_count ?? 0;
    const uldGroups = buildUldGroups(cpm, ffm);
    const loadIncomplete = fact_summary?.load?.status === "INCOMPLETE"
        || (cpm?.missing_fields || []).includes("position_code");
    const positionUtilization = loadIncomplete ? null : summary.position_utilization;

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {showSummary && (
                <Card size="small" title="总览">
                    <Row gutter={16}>
                        <Col span={5}>
                            <Tooltip title="装载毛重（CPM 或附件装载重量表）">
                                <Statistic title="装载毛重" value={summary.gross_weight_kg} suffix="kg" precision={0} />
                            </Tooltip>
                        </Col>
                        <Col span={5}>
                            <Tooltip title="运单重量（FFM 或附件舱单）">
                                <Statistic title="运单重量" value={summary.cargo_weight_kg} suffix="kg" precision={0} />
                            </Tooltip>
                        </Col>
                        <Col span={3}>
                            <Statistic title="ULD" value={summary.uld_count} suffix="个" />
                        </Col>
                        <Col span={3}>
                            <Statistic title="运单数" value={summary.awb_count} suffix="票" />
                        </Col>
                        <Col span={4}>
                            <Statistic
                                title="舱位利用率"
                                value={positionUtilization}
                                suffix={positionUtilization != null ? "%" : undefined}
                                precision={1}
                                formatter={positionUtilization == null ? () => "—" : undefined}
                            />
                        </Col>
                        <Col span={4}>
                            <Tooltip title={`${fwbLinked} / ${awbTotal} 票运单已关联 FWB 数据`}>
                                <Statistic
                                    title="FWB 覆盖"
                                    value={fwbLinked}
                                    suffix={`/ ${awbTotal}`}
                                    valueStyle={fwbLinked > 0 ? { color: "#52c41a" } : undefined}
                                />
                            </Tooltip>
                        </Col>
                    </Row>
                    {summary.total_pieces != null && (
                        <Row style={{ marginTop: 8 }}>
                            <Col>
                                <Text type="secondary">总件数: {summary.total_pieces}</Text>
                            </Col>
                        </Row>
                    )}
                    <div style={{ marginTop: 8 }}>
                        {(data_sources || []).map((s, i) => (
                            <Tag key={i} color={s.type === "CPM" ? "blue" : "green"}>
                                {s.type} · {s.source_kind === "MAIL_ATTACHMENT" ? "附件" : (s.source || "报文")}
                            </Tag>
                        ))}
                        {fwbLinked > 0 && <Tag color="orange">FWB · {fwbLinked} 票</Tag>}
                    </div>
                </Card>
            )}

            {uldGroups.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <Space wrap>
                        <Text strong>ULD 装载明细</Text>
                        {cpm && <Tag color="blue">CPM：{cpm.loaded_count ?? cpm.positions?.length ?? 0} 装载 / {cpm.empty_count ?? 0} 空</Tag>}
                        {ffm && <Tag color="green">FFM：{awbTotal} 票主单</Tag>}
                        {loadIncomplete && <Tag color="orange">装载不完整（缺舱位）</Tag>}
                        {fact_summary?.uld_comparison?.status === "MISMATCH" && (
                            <Tooltip title={
                                [
                                    fact_summary.uld_comparison.cpm_only?.length > 0
                                        && `CPM 有但 FFM 无：${fact_summary.uld_comparison.cpm_only.join(", ")}`,
                                    fact_summary.uld_comparison.ffm_only?.length > 0
                                        && `FFM 有但 CPM 无：${fact_summary.uld_comparison.ffm_only.join(", ")}`,
                                ].filter(Boolean).join("；")
                            }>
                                <Tag color="orange">ULD 清单不一致</Tag>
                            </Tooltip>
                        )}
                        {ffm_completeness?.status === "partial_merged" && (
                            <Tag color="orange">
                                报文段不完整（共 {ffm_completeness.segment_count} 段，部分缺失）
                            </Tag>
                        )}
                    </Space>
                    <Collapse
                        size="small"
                        items={uldGroups.map((g) => buildUldCollapseItem(g, canDownload))}
                    />
                </div>
            )}

            {uldGroups.length === 0 && (cpm || ffm) && (
                <Empty description="暂无已装载 ULD 或主单数据" />
            )}

            {!ffm && ffm_completeness?.status === "waiting_segments" && (
                <Card size="small">
                    <Tag color="default">
                        已收到 {ffm_completeness.waiting_count} 段报文，等待后续段到达
                    </Tag>
                </Card>
            )}
        </div>
    );
}
